import AnimatedRoute, { AnimatedRouteProps } from '@components/AnimatedRoute'
import ContactDetails from '@components/ContactDetails'
import RecentActivity from '@components/RecentActivity'
import { Sidebar } from '@components/Sidebar'
import SpotlightSearch from '@components/SpotlightSearch'
import { StatusPill } from '@components/StatusPill'
import usePrevious from '@hooks/usePrevious'
import { useResponsiveLayout } from '@hooks/useResponsiveLayout'
import { AccountForm } from '@modules/AccountForm'
import AccountList from '@modules/AccountList'
import Authorize from '@modules/Authorize'
import BorrowLend from '@modules/BorrowLend'
import FundBorrowLend from '@modules/BorrowLend/Fund'
import ConnectedApps from '@modules/ConnectedApps'
import { Contacts } from '@modules/Contacts'
import { Discover } from '@modules/Discover'
import { Earn } from '@modules/Earn'
import { EarnDeposit } from '@modules/Earn/Components/EarnDeposit'
import { EarnWithdraw } from '@modules/Earn/Components/EarnWithdraw'
import { VaultsList } from '@modules/Earn/Components/VaultsList'
import { HomeOverview } from '@modules/Home/Components'
import Addresses from '@modules/Home/Components/Addresses'
import { DeFiHub } from '@modules/Home/Components/DeFiHub'
import KeyRevealAcceptance from '@modules/KeyRevealAcceptance'
import LLMChat from '@modules/LLMChat'
import LocaleSettings from '@modules/LocaleSettings'
import { ManageEVM } from '@modules/ManageEVM'
import MintCycles from '@modules/MintCycles'
import { NftsOverview } from '@modules/Nfts/Components'
import { NftDetails } from '@modules/Nfts/Components/NftDetails'
import PerpsList from '@modules/Perps'
import PerpDetails from '@modules/Perps/Components/PerpDetails'
import Send from '@modules/Send'
import Settings from '@modules/Settings'
import DeveloperSettings from '@modules/Settings/Components/DeveloperSettings'
import NetworkForm from '@modules/Settings/Components/NetworkForm'
import NetworkList from '@modules/Settings/Components/NetworkList'
import { Swap } from '@modules/Swap'
import ICPSwapFallback from '@modules/SwapFallback/ICPSwapFallback'
import PairPicker from '@modules/SwapFallback/PairPicker'
import ProviderPicker from '@modules/SwapFallback/ProviderPicker'
import { TokenDetails } from '@modules/Tokens'
import { TokenManager } from '@modules/Tokens/Components/TokenManager'
import { CanisterTopupPage } from '@modules/TopUp'
import { TopUpHistory } from '@modules/TopUp/TopUpHistory'
import { UserActivity } from '@modules/UserActivity'
import WalletImportPrivateKey from '@modules/Wallet/Components/WalletImportPrivateKey'
import WalletImportStoic from '@modules/Wallet/Components/WalletImportStoic'
import WalletImportTypePicker from '@modules/Wallet/Components/WalletImportTypePicker'
import { WalletSetupBackupSeed } from '@modules/WalletSetup/components/BackupSeed'
import { WaterNeuronContainer } from '@modules/WaterNeuron'
import LedgerScannerClient from '@plug-core/clients//LedgerScannerClient'
import { LiquidiumMode } from '@plug-core/hooks/useLiquidiumAction'
import useWallet from '@plug-core/hooks/useWallet'
import { GAEventName } from '@plug-core/interfaces/googleAnalytics'
import { logger } from '@plug-core/logger'
import { useGoogleAnalytics } from '@plug-core/providers/GoogleAnalyticsProvider'
import { usePendingRequests } from '@plug-core/state'
import { getPageInfo } from '@plug-core/utils/getPageInfo'
import { useMobileSidebar } from '@providers/MobileSidebarProvider'
import { useSpotlightShortcut } from '@providers/SpotlightProvider'
import { cn, Sheet } from '@ui'
import { ROUTES } from '@utils/routes'
import i18next from 'i18next'
import React, { useEffect } from 'react'
import {
  createHashRouter,
  isRouteErrorResponse,
  Navigate,
  Outlet,
  RouteObject,
  useLocation,
  useNavigate,
  useOutlet,
  useRouteError,
} from 'react-router-dom'

const RootHandler = () => {
  return null
}

const ParentRoute = ({ children, ...rest }: AnimatedRouteProps) => {
  const outlet = useOutlet()

  return <AnimatedRoute {...rest}>{outlet || children}</AnimatedRoute>
}

export const ROUTE_DATA: RouteObject[] = [
  {
    path: ROUTES.ROOT,
    element: <RootHandler />,
    index: true,
  },
  {
    path: ROUTES.HOME,
    element: (
      <ParentRoute animationDirection="base" navbar={false}>
        <HomeOverview />
      </ParentRoute>
    ),
    children: [
      {
        path: ROUTES.TOKEN_DETAILS(':tokenId'),
        element: (
          <AnimatedRoute animationDirection="up" navbar={false}>
            <TokenDetails />
          </AnimatedRoute>
        ),
      },
      {
        path: ROUTES.MANAGE_TOKENS,
        element: (
          <AnimatedRoute animationDirection="up" title={i18next.t('manageTokens.title')}>
            <TokenManager />
          </AnimatedRoute>
        ),
      },
      {
        path: ROUTES.SEND,
        element: (
          <AnimatedRoute animationDirection="up" navbar={false}>
            <Send />
          </AnimatedRoute>
        ),
      },
      {
        path: ROUTES.SWAP,
        element: (
          <ParentRoute animationDirection="up" title="Swap" navbar={false}>
            <Swap />
          </ParentRoute>
        ),
        children: [
          {
            path: ROUTES.SWAP_FALLBACK_PROVIDER_SELECT,
            element: (
              <AnimatedRoute animationDirection="right" title="Select Swap Provider">
                <ProviderPicker />
              </AnimatedRoute>
            ),
          },
          {
            path: ROUTES.SWAP_FALLBACK_PAIR_SELECT,
            element: (
              <AnimatedRoute animationDirection="right" title="Select Swap Pair">
                <PairPicker />
              </AnimatedRoute>
            ),
          },
          {
            path: ROUTES.ICPSWAP_FALLBACK_PAIR,
            element: (
              <AnimatedRoute animationDirection="right" title="Fallback">
                <ICPSwapFallback />
              </AnimatedRoute>
            ),
          },
        ],
      },
      {
        path: ROUTES.ADDRESSES,
        element: (
          <AnimatedRoute animationDirection="right">
            <Addresses />
          </AnimatedRoute>
        ),
      },
    ],
  },
  {
    path: ROUTES.CONTACTS,
    element: (
      <ParentRoute animationDirection="base" navbar={false}>
        <Contacts />
      </ParentRoute>
    ),
    children: [
      {
        path: ROUTES.CONTACT_DETAILS(':id'),
        element: (
          <AnimatedRoute animationDirection="right">
            <ContactDetails />
          </AnimatedRoute>
        ),
      },
    ],
  },
  {
    path: ROUTES.NFT_OVERVIEW,
    element: (
      <ParentRoute animationDirection="base" navbar={false}>
        <NftsOverview />
      </ParentRoute>
    ),
    children: [
      {
        path: ROUTES.NFT_DETAILS(':nftCanisterId', ':tokenId'),
        element: (
          <AnimatedRoute animationDirection="right">
            <NftDetails />
          </AnimatedRoute>
        ),
      },
    ],
  },
  {
    path: ROUTES.USER_ACTIVITY,
    element: (
      <AnimatedRoute animationDirection="base" navbar={false}>
        <UserActivity />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.DISCOVER,
    element: (
      <AnimatedRoute animationDirection="base" navbar={false}>
        <Discover />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.BACKUP_SEED,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <WalletSetupBackupSeed />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.AUTHORIZE,
    element: (
      <AnimatedRoute animationDirection="base" navbar={false}>
        <Authorize />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.SETTINGS,
    element: (
      <AnimatedRoute animationDirection="base" navbar={false}>
        <Settings />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.ACCOUNTS,
    element: (
      <ParentRoute animationDirection="up" title="Accounts">
        <AccountList />
      </ParentRoute>
    ),
    children: [
      {
        path: ROUTES.EDIT_ACCOUNT(':principalId'),
        element: (
          <AnimatedRoute animationDirection="right" title="Edit account" navbar={false}>
            <AccountForm />
          </AnimatedRoute>
        ),
      },
      {
        path: ROUTES.WALLET_IMPORT_TYPE,
        element: (
          <AnimatedRoute animationDirection="right" title="Add another wallet" navbar={false}>
            <WalletImportTypePicker />
          </AnimatedRoute>
        ),
      },
    ],
  },
  {
    path: ROUTES.KEY_REVEAL_ACCEPTANCE,
    element: (
      <AnimatedRoute animationDirection="right">
        <KeyRevealAcceptance />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.STAKE,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <WaterNeuronContainer />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.EARN,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <Earn />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.EARN_VAULTS,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <VaultsList />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.EARN_DEPOSIT,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <EarnDeposit />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.EARN_WITHDRAW,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <EarnWithdraw />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.BORROW_LEND,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <BorrowLend />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.FUND_BORROW_LEND(':mode' as LiquidiumMode),
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <FundBorrowLend />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.TOPUP,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <CanisterTopupPage />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.TOPUP_HISTORY,
    element: (
      <AnimatedRoute title="Top Up History" animationDirection="right">
        <TopUpHistory />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.MINT_CYCLES,
    element: (
      <AnimatedRoute title="Mint Cycles" animationDirection="right">
        <MintCycles />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.CONNECTED_APPS,
    element: (
      <AnimatedRoute title="Connected dApps" animationDirection="right">
        <ConnectedApps />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.DEVELOPER_SETTINGS,
    element: (
      <AnimatedRoute title="Developer Settings" animationDirection="right">
        <DeveloperSettings />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.DEVELOPER_SETTINGS_NETWORKS,
    element: (
      <AnimatedRoute title="Networks" animationDirection="right">
        <NetworkList />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.DEVELOPER_SETTINGS_NEW_NETWORK,
    element: (
      <AnimatedRoute title="New Network" animationDirection="right">
        <NetworkForm />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.DEVELOPER_SETTINGS_EDIT_NETWORK(':networkId'),
    element: (
      <AnimatedRoute title="Edit Network" animationDirection="right">
        <NetworkForm />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.LOCALE_SETTINGS,
    element: (
      <AnimatedRoute title="Select currency" animationDirection="right">
        <LocaleSettings />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.PEM_IMPORT,
    element: (
      <AnimatedRoute title="Import Private Key" animationDirection="right">
        <WalletImportPrivateKey />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.STOIC_IMPORT,
    element: (
      <AnimatedRoute title="Import Stoic wallet" animationDirection="right">
        <WalletImportStoic />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.LLM_CHAT,
    element: (
      <AnimatedRoute title="Plug Intelligence" animationDirection="right">
        <LLMChat />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.MANAGE_EVM,
    element: (
      <AnimatedRoute title="Manage Chains" animationDirection="right">
        <ManageEVM />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.DEFI_HUB,
    element: (
      <AnimatedRoute title="DeFi Hub" animationDirection="base" navbar={false}>
        <DeFiHub />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.PERPS,
    element: (
      <AnimatedRoute animationDirection="up" navbar={false}>
        <PerpsList />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.PERP_DETAILS(':hyperliquidTokenId'),
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <PerpDetails />
      </AnimatedRoute>
    ),
  },
]

const RootLayout = () => {
  const { fireEvent } = useGoogleAnalytics()
  const { key, pathname } = useLocation()
  const { isDesktop, isTablet, isMobile } = useResponsiveLayout()
  const navigate = useNavigate()
  const spotlight = useSpotlightShortcut()
  const mobileSidebar = useMobileSidebar()

  const { wallet } = useWallet()

  const { pendingRequests } = usePendingRequests()
  const prevPendingRequest = usePrevious(pendingRequests?.[0])

  useEffect(() => {
    if (pendingRequests?.[0] && pendingRequests?.[0].id !== prevPendingRequest?.id) {
      navigate(ROUTES.AUTHORIZE)
    }
  }, [navigate, pendingRequests, prevPendingRequest?.id])

  useEffect(() => {
    fireEvent(GAEventName.PAGE_VIEW, getPageInfo(ROUTES, pathname))
  }, [pathname])

  useEffect(() => {
    LedgerScannerClient.registerUser(wallet.principalId, wallet.accountId).catch((e) => {
      if (!e.toString().includes('User already exists')) {
        logger.debug(e)
      }
    })
  }, [wallet.principalId])

  if (pathname === ROUTES.ROOT) {
    return <Navigate to={ROUTES.HOME} replace />
  }

  return (
    <>
      {(isDesktop || isTablet) && <Sidebar />}

      {isMobile && (
        <Sheet
          side="left"
          open={mobileSidebar.isOpen}
          onClose={mobileSidebar.close}
          title="Menu"
          hideTitle
        >
          <Sidebar inSheet />
        </Sheet>
      )}

      <div className="flex w-full flex-row overflow-auto">
        {/* Non-scrolling frame around the column, so the status pill stays over its header row. */}
        <div
          className={cn(
            'relative mx-auto flex w-full',
            isTablet || isDesktop ? 'max-w-[600px]' : 'max-w-full'
          )}
        >
          <div className="relative w-full overflow-y-scroll pt-[10px] sm:pt-[30px]">
            <div className="mx-3 sm:mx-5 lg:mx-0">
              <Outlet key={key} />
            </div>
          </div>

          <StatusPill />
        </div>

        {isDesktop && (
          <div className="mt-[30px]">
            <RecentActivity />
          </div>
        )}
      </div>

      <SpotlightSearch open={spotlight.isOpen} onClose={spotlight.close} />
    </>
  )
}

export const Rerouter = ({ root }: { root: string }) => {
  const navigate = useNavigate()
  const error = useRouteError() as Error

  useEffect(() => {
    if (error && !isRouteErrorResponse(error)) {
      logger.error('Error Boundary', { originalError: error })
    }
  }, [error])

  React.useEffect(() => {
    navigate(root)
  }, [])

  return null
}

export const router = createHashRouter([
  {
    element: <RootLayout />,
    children: ROUTE_DATA,
    errorElement: <Rerouter root={ROUTES.ROOT} />,
  },
])
